import { ApiRequest, shiftApi } from '@/api';
import i18n from '@/i18n';
import { dateTimeFormats } from '@/lang/dateTimeFormats';
import {
  absenceEndDate,
  absenceStartDate,
} from '@/lib/absence/absenceFunctions';
import {
  employeeAttributeFilter,
  fullName,
  whereEmploymentType,
} from '@/lib/employee/employeeFunctions';
import { ScheduleViewMode } from '@/lib/enum/schedule-v2/ScheduleViewMode';
import { isInPeriod, startsInPeriod } from '@/lib/filters/dateFilters';
import { belongsToEmployee } from '@/lib/filters/employeeFilters';
import { getFilterValues } from '@/lib/filters/filterHelpers';
import {
  leaveEndDate,
  leaveStartDate,
} from '@/lib/leave/leaveRequestFunctions';
import { userCan } from '@/lib/permission/userCan';
import { ScheduleV2FilterType } from '@/lib/schedule-v2/ScheduleV2Filter';
import {
  ScheduleGroupByEnum,
  ViewShiftsByEnum,
} from '@/lib/schedule-v2/ScheduleViewOptions';
import { shiftHasConflict } from '@/lib/schedule-v2/shiftV2Functions';
import {
  isAssigned,
  isOpenAndUnassigned,
  isPublished,
} from '@/lib/schedule-v2/shiftV2Status';
import { employeeGroupFilter } from '@/lib/team/teamFunctions';
import { ColumnHeader } from '@/types/schedule';
import { sortBy } from '@/util/arrayFunctions';
import { isSameTime } from '@/util/dateFunctions';
import { getEntity } from '@/util/entityFunctions';
import spacetime from 'spacetime';
import { Spacetime } from 'spacetime/types/types.d';
import {
  Absence,
  Employee,
  EmployeeGroup,
  EmployeeStatusEnum,
  JobRole,
  LeaveRequest,
  ScheduleSettings,
  Shift,
  ShiftAreaSession,
  Unavailability,
} from '../../../api/v1';

// Various filters that can be applied to entities like Employees, Shifts, or Schedule Events
export const assignedFilter =
  (showUnassigned: boolean, assignedEmployeeIds: number[]) =>
  (employee: Employee) =>
    showUnassigned || assignedEmployeeIds.includes(employee.id);

export const jobRoleFilter =
  (selectedIds: Array<number | null>) =>
  ({ jobRoleId }: { jobRoleId: number }) =>
    selectedIds.length === 0 ||
    selectedIds.includes(null) ||
    selectedIds.includes(jobRoleId);
export const scheduleFilter =
  (activeScheduleIds: number[], selectedId: number | null) =>
  ({ scheduleId }: { scheduleId: number }) =>
    [null, ...activeScheduleIds].includes(scheduleId) &&
    [null, scheduleId].includes(selectedId);

export const scheduleFilterMultiple =
  (activeScheduleIds: number[], selectedId: number | null) =>
  ({ scheduleIds }: { scheduleIds: number[] }) =>
    activeScheduleIds.some((id) => scheduleIds.includes(id)) &&
    [null, ...scheduleIds].includes(selectedId);

export const employeeScheduleFilter =
  (selectedId: number | null) =>
  ({ scheduleIds }: Employee): boolean => {
    return [null, ...scheduleIds].includes(selectedId);
  };

export const scheduleEmployeeStatusFilter =
  (includePending: boolean, shifts: Shift[], startDate: Date) =>
  (employee: Employee): boolean => {
    return (
      {
        [EmployeeStatusEnum.Active]: true,
        [EmployeeStatusEnum.Pending]: includePending,
        //  Some archived employees don't have an archivedAt value due to legacy code
        //  so add a safety net to show archived if they have shifts
        [EmployeeStatusEnum.Archived]:
          (employee.archivedAt && employee.archivedAt > startDate) ||
          !!shifts.find((s) => s.employeeId === employee.id),
      }[employee.status] ?? false
    );
  };

/**
 * Filters employees for the schedule based on their join date.
 * If it's the current logged in employee, they have shifts or no join date then display anyway.
 * @param dateRange
 * @param loggedInEmployee
 * @param shifts
 * @param timezone
 */
export const scheduleEmployeeJoinDateFilter =
  (
    dateRange: { startDate: Date; endDate: Date },
    loggedInEmployee: Employee,
    shifts: Shift[],
    timezone: string,
  ) =>
  (employee: Employee) =>
    loggedInEmployee.id === employee.id ||
    shifts.some(
      (s) =>
        belongsToEmployee(employee.id)(s) &&
        startsInPeriod(dateRange.startDate, dateRange.endDate)(s),
    ) ||
    !employee.joinDate ||
    employee.joinDate.toDate(timezone) < dateRange.endDate;

export const tagFilter =
  (selectedIds: number[]) =>
  ({ tagIds }: { tagIds: number[] }) =>
    selectedIds.length === 0 || selectedIds.some((id) => tagIds.includes(id));
export const publishedFilter = (showUnpublished: boolean) => (shift: Shift) =>
  showUnpublished || shift.publishedAt;

// Not already published, is assigned, same schedule, starts in future and has no conflicts
export const publishShiftFilter =
  (now: Date, scheduleId: Number, includeOpenShifts: boolean = true) =>
  (shift: Shift): boolean =>
    !shift.publishedAt &&
    shift.scheduleId === scheduleId &&
    shift.startsAt > now &&
    ((includeOpenShifts && isOpenAndUnassigned(shift)) ||
      (isAssigned(shift) && !shiftHasConflict(shift)));

export const locationFilter = (locationId: number[]) => (shift: Shift) =>
  !locationId.length || locationId.includes(shift.locationId);

export const employeeShiftFilter = (employeeIds: number[]) => (shift: Shift) =>
  !employeeIds.length || employeeIds.includes(shift.employeeId);

export const dayOfWeekFilter =
  (daysOfWeek: string[], timezone: string) =>
  (shift: Shift): boolean => {
    const shiftWeekDay = spacetime(shift.startsAt, timezone).format('day');
    return !daysOfWeek.length || daysOfWeek.includes(shiftWeekDay);
  };

export const shiftTimeFilter =
  (
    { startTime, endTime }: { startTime: null | Date; endTime: null | Date },
    timezone: string,
  ) =>
  (shift: Shift): boolean =>
    (!startTime ||
      isSameTime(
        spacetime(new Date(), timezone)
          .time(spacetime(shift.startsAt, timezone).time())
          .toNativeDate(),
        spacetime(new Date(), timezone)
          .time(spacetime(startTime, timezone).time())
          .toNativeDate(),
        timezone,
      )) &&
    (!endTime ||
      isSameTime(
        spacetime(new Date(), timezone)
          .time(spacetime(shift.endsAt, timezone).time())
          .toNativeDate(),
        spacetime(new Date(), timezone)
          .time(spacetime(endTime, timezone).time())
          .toNativeDate(),
        timezone,
      ));

export enum ShiftStatusEnum {
  All = 'All',
  Published = 'Published',
  Unpublished = 'Unpublished',
}
export const shiftStatuses = Object.values(ShiftStatusEnum).map((value) => ({
  id: value,
  value,
  name: i18n.tc(`enum.shiftStatus.${value}`, 1),
}));
export const shiftStatusFilter =
  (status: ShiftStatusEnum) =>
  (shift: Shift): boolean => {
    if (status === ShiftStatusEnum.Published) {
      return isPublished(shift);
    }
    if (status === ShiftStatusEnum.Unpublished) {
      return !isPublished(shift);
    }
    return true;
  };

export const shiftFilter =
  (filter: ScheduleV2FilterType) =>
  (shift: Shift): boolean =>
    [shift]
      .filter(jobRoleFilter(getFilterValues(filter.jobRoleIds)))
      .filter(tagFilter(getFilterValues(filter.tagIds)))
      .filter(locationFilter(getFilterValues(filter.locationId))).length > 0;

// Visible shifts for employees
export const employeeCanViewShiftForOtherLocations =
  (
    employee: Employee,
    { employeesCanViewShiftsForOtherLocations }: ScheduleSettings,
    hasPermission: boolean = userCan.manageShifts(),
  ) =>
  ({ locationId }: { locationId: number }): boolean =>
    hasPermission ||
    employeesCanViewShiftsForOtherLocations ||
    employee.locationIds.includes(locationId);

export const employeeCanViewShiftForOtherSchedules =
  (
    employee: Employee,
    { employeesCanViewShiftsForOtherSchedules }: ScheduleSettings,
    hasPermission: boolean = userCan.manageShifts(),
  ) =>
  ({ scheduleId }: { scheduleId: number }): boolean =>
    hasPermission ||
    employeesCanViewShiftsForOtherSchedules ||
    employee.scheduleIds.includes(scheduleId);

export const scheduleEmployeeFilter =
  (filter: ScheduleV2FilterType, excludes: string[] = []) =>
  (employee: Employee | null): boolean => {
    if (!employee) {
      return ![
        ...filter.employeeAttributeIds,
        ...filter.employmentTypes,
        ...filter.employeeGroupIds,
        ...filter.employeeIds,
        ...(excludes.includes('employeeJobRoleIds')
          ? []
          : filter.employeeJobRoleIds),
      ].length;
    }
    return (
      [employee]
        // User selected filters
        .filter(
          employeeAttributeFilter(getFilterValues(filter.employeeAttributeIds)),
        )
        .filter(whereEmploymentType(getFilterValues(filter.employmentTypes)))
        .filter(employeeGroupFilter(getFilterValues(filter.employeeGroupIds)))
        .filter(
          (e) =>
            !filter.employeeIds.length ||
            getFilterValues(filter.employeeIds).includes(e.id),
        )
        .filter(
          (e) =>
            excludes.includes('employeeJobRoleIds') ||
            !filter.employeeJobRoleIds.length ||
            e.jobRoleIds.some((jr) =>
              getFilterValues(filter.employeeJobRoleIds).includes(jr),
            ),
        ).length > 0
    );
  };

export const shiftAreaFilter =
  (filter: ScheduleV2FilterType) =>
  (shift: Shift): boolean =>
    [shift]
      .filter(jobRoleFilter(getFilterValues(filter.jobRoleIds)))
      .filter(locationFilter(getFilterValues(filter.locationId)))
      .filter(employeeShiftFilter(getFilterValues(filter.employeeIds))).length >
    0;

export const shiftIsIncludedInFilter = (
  shift: Shift,
  filter: ScheduleV2FilterType,
): boolean => {
  return shiftFilter(filter)(shift);
};

/**
 * Whether the filters leave a shift on show, narrowing the way each view's grid does.
 * Unassigned and open shifts sit in rows of their own on the employee view, which the
 * employee filters leave alone. The area view only shows the picked areas' rows, so a
 * shift has to be in one of them.
 */
export const shiftMatchesScheduleFilters =
  (
    filter: ScheduleV2FilterType,
    viewShiftsBy: ViewShiftsByEnum,
    employees: Employee[],
  ) =>
  (shift: Shift): boolean => {
    if (viewShiftsBy === ViewShiftsByEnum.ShiftArea) {
      return (
        shiftAreaFilter(filter)(shift) &&
        (!filter.areaIds.length ||
          (shift.shiftAreaSessions ?? []).some(({ shiftAreaId }) =>
            getFilterValues(filter.areaIds).includes(shiftAreaId),
          ))
      );
    }
    if (!shiftIsIncludedInFilter(shift, filter)) {
      return false;
    }
    if (viewShiftsBy === ViewShiftsByEnum.Employee && !shift.employeeId) {
      return true;
    }
    return scheduleEmployeeFilter(filter)(
      getEntity(shift.employeeId, employees) ?? null,
    );
  };

/**
 * Whether the employee filters leave an employee on show. The area view narrows by
 * the picked employees alone, as its grid does through shiftAreaFilter.
 */
export const employeeMatchesScheduleFilters =
  (filter: ScheduleV2FilterType, viewShiftsBy: ViewShiftsByEnum) =>
  (employee: Employee | null): boolean => {
    if (viewShiftsBy === ViewShiftsByEnum.ShiftArea) {
      return (
        !filter.employeeIds.length ||
        (!!employee &&
          getFilterValues(filter.employeeIds).includes(employee.id))
      );
    }
    return scheduleEmployeeFilter(filter)(employee);
  };

// Sort functions
export const employeeNameSort = (employeeA: Employee, employeeB: Employee) =>
  fullName(employeeA).localeCompare(fullName(employeeB));

export const shiftsCanBeGrouped = <T extends Partial<Shift>>(
  a: T,
  b: T,
): boolean =>
  // First compare the shift data
  a.comparisonHash === b.comparisonHash &&
  // Then compare shift tags a to b and vice versa to ensure all match
  a.tagIds.every((aTagId) => b.tagIds.some((bTagId) => aTagId === bTagId)) &&
  b.tagIds.every((bTagId) => a.tagIds.some((aTagId) => bTagId === aTagId)) &&
  // Then compare shift breaks a to b and vice versa to ensure all match
  a.scheduledBreaks.every((aBreak) =>
    b.scheduledBreaks.some(
      (bBreak) => aBreak.comparisonHash === bBreak.comparisonHash,
    ),
  ) &&
  b.scheduledBreaks.every((bBreak) =>
    a.scheduledBreaks.some(
      (aBreak) => bBreak.comparisonHash === aBreak.comparisonHash,
    ),
  ) &&
  // Then compare shift area session a to b and vice versa to ensure all match
  // Area sessions could be undefined so ensure that matches before comparing contents
  typeof a.shiftAreaSessions === typeof b.shiftAreaSessions &&
  a.shiftAreaSessions.every((aSession) =>
    b.shiftAreaSessions.some(
      (bSession) => aSession.comparisonHash === bSession.comparisonHash,
    ),
  ) &&
  b.shiftAreaSessions.every((bSession) =>
    a.shiftAreaSessions.some(
      (aSession) => bSession.comparisonHash === aSession.comparisonHash,
    ),
  );

/** Get an array of ids matching the provided shift */
export const matchingShiftIds = (shift: Shift, shifts: Shift[]): number[] =>
  shifts
    .filter((s) => s.id !== shift.id && shiftsCanBeGrouped(s, shift))
    .map((s) => s.id);
/** Checks shift does not have matching previous shifts in an array */
export const doesNotHaveMatchingPreviousShifts = (
  shift: Shift,
  shifts: Shift[],
): boolean =>
  shifts.filter((s) => s.id < shift.id && shiftsCanBeGrouped(s, shift))
    .length === 0;

/** Get a series of date objects that represent the schedule view mode */
export const getDateHeaders = (
  date: Date,
  timezone: string,
  viewMode: ScheduleViewMode,
  dayFormat: dateTimeFormats,
  dateFormat: dateTimeFormats,
): ColumnHeader[] => {
  const weekStart = spacetime(date, timezone).startOf('day');
  const endDate =
    {
      [ScheduleViewMode.Week]: weekStart.add(7, 'day'),
      [ScheduleViewMode.Month]: weekStart.add(1, 'month'),
    }[viewMode] || weekStart;

  const dates = weekStart.every(endDate, 'day', 1);

  return dates.map((start: Spacetime) => {
    // If crossover DST this becomes 01:00 so set back to start of day
    start = start.startOf('day');
    const end = start.add(1, 'day').startOf('day');
    return {
      dayName: i18n.d(start.toNativeDate(), dayFormat),
      dateString: i18n.d(start.toNativeDate(), dateFormat),
      start: start.toNativeDate(),
      end: end.toNativeDate(),
    };
  });
};

export const getShiftsForAcknowledgement = async (
  employeeId: number,
): Promise<Array<Shift>> => {
  const { data } = await new ApiRequest(shiftApi, 'listShifts')
    .where('employeeId', 'eq', employeeId)
    .where('startsAt', 'gte', new Date())
    .whereNull('acknowledgedAt')
    .whereNotNull('publishedAt')
    .orderBy('startsAt')
    .fetchAll();

  return data;
};

export const presentShiftTimes = ({
  startsAt,
  endsAt,
  showEndTime,
}: {
  startsAt: Date;
  endsAt: Date;
  showEndTime: boolean;
}): string => {
  const start = i18n.d(startsAt, dateTimeFormats.hourMinute);
  const end = i18n.d(endsAt, dateTimeFormats.hourMinute);

  return start + (showEndTime || userCan.manageShifts() ? ` - ${end}` : '');
};

/**
 * This is almost identical to our normal presentShiftTimes function, except that we assume the print-out is going
 * to be viewed by non-managers, and so here we always obey the shift's own setting for whether to show end times.
 */
export const presentShiftTimesForPrinting = (
  { startsAt, endsAt, showEndTime }: Shift,
  ignoreShowShiftEndTimeField: boolean,
): string => {
  const start = i18n.d(startsAt, dateTimeFormats.hourMinute);
  const end =
    showEndTime || ignoreShowShiftEndTimeField
      ? `- ${i18n.d(endsAt, dateTimeFormats.hourMinute)}`
      : '→';
  return `${start} ${end}`;
};

export const isNightShift = (shift: Shift, timezone: string): boolean =>
  !spacetime(shift.startsAt, timezone).isSame(
    spacetime(shift.endsAt, timezone),
    'day',
  );
export const isNightAreaSession = (
  session: ShiftAreaSession,
  timezone: string,
): boolean =>
  !spacetime(session.startTime, timezone).isSame(
    spacetime(session.endTime, timezone),
    'day',
  );

export const employeeIsAvailable = (
  employeeId: number,
  start: Date,
  end: Date,
  timezone: string,
  entities: {
    unavailability: Unavailability[];
    leaveRequests: LeaveRequest[];
    shifts: Shift[];
    absences: Absence[];
  },
  endInclusive: boolean = false,
): boolean => {
  return (
    !entities.unavailability.some(
      (u) =>
        belongsToEmployee(employeeId)(u) &&
        u.startsAt < end &&
        u.endsAt > start,
    ) &&
    !entities.leaveRequests.some(
      (l) =>
        belongsToEmployee(employeeId)(l) &&
        leaveStartDate(l, timezone) < end &&
        leaveEndDate(l, timezone) > start,
    ) &&
    !entities.shifts.some(
      (s) =>
        belongsToEmployee(employeeId)(s) &&
        (endInclusive
          ? isInPeriod(start, end, 'startsAt', 'endsAt')(s)
          : startsInPeriod(start, end, 'startsAt')(s)),
    ) &&
    !entities.absences.some(
      (a) =>
        belongsToEmployee(employeeId)(a) &&
        absenceStartDate(a, timezone) < end &&
        (!a.endedOn || absenceEndDate(a, timezone) > start),
    )
  );
};

export const getScheduleGroupOrder = (
  groupBy: ScheduleGroupByEnum,
  key: string,
  jobRoles: JobRole[],
  groups: EmployeeGroup[],
): string => {
  const findEntity = <T extends JobRole | EmployeeGroup>(
    id: string,
    entities: T[],
  ): T | null =>
    id === 'null' ? null : getEntity(Number(id), entities) ?? null;

  if (groupBy === ScheduleGroupByEnum.Group) {
    const maxOrder =
      (sortBy(groups, 'position')[groups.length - 1]?.position ?? 0) + 1;
    const group = findEntity(key, groups);
    return `order: ${group ? group.position : maxOrder};`;
  }
  if (groupBy === ScheduleGroupByEnum.JobRole) {
    const maxOrder =
      (sortBy(jobRoles, 'position')[jobRoles.length - 1]?.position ?? 0) + 1;
    const role = findEntity(key, jobRoles);
    return `order: ${role ? role.position : maxOrder};`;
  }
  return '';
};

export const calculateNewShiftDates = (
  originalShift: Shift,
  newStart: Date,
  timezone: string,
): { start: Date; end: Date } => {
  // Calculate new shift start (on the new calendar day, but for the original time)
  const originalShiftStart = spacetime(originalShift.startsAt, timezone);
  const originalShiftEnd = spacetime(originalShift.endsAt, timezone);

  const originalStartPeriod = originalShiftStart.startOf('day');
  const newStartPeriod = spacetime(newStart, timezone).startOf('day');

  // We need to allow for DST changes, which is why we calculate the offset in days
  const startOffsetInDays = originalStartPeriod.diff(newStartPeriod, 'day');

  const start = originalShiftStart.add(startOffsetInDays, 'day');
  let end = originalShiftEnd.add(startOffsetInDays, 'day');

  if (end.isBefore(start)) {
    end = end.add(1, 'day');
  }

  return { start: start.toNativeDate(), end: end.toNativeDate() };
};
